(ns frontend.view.modals.entity.view
  (:require
   [clojure.set :as set]
   [clojure.string :as string]
   [common.entity :as entity]
   [common.features.plan :as plan]
   [common.intelligence.common :as ic]
   [common.localization :as loc]
   [common.roles :as roles]
   [common.schema.constraints :as constraints]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.entities.reminder-rule.options :as reminder-rule-options]
   [common.schema.field :as field]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.components.field-editor :as field-editor]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.utils :as fu]
   [frontend.view.entity-viewers.utils :as entity-utils]
   [frontend.view.modals.entity.attributes :as attributes]
   [frontend.view.modals.entity.create-attributes :as create-attributes]
   [frontend.view.modals.entity.events :as events]
   [frontend.view.modals.entity.schema :as schema]
   [frontend.view.syncing-data :as syncing]
   [frontend.zap-time :as zap-time]
   [potpuri.core :refer [fn->]]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [reagent.dom.server :as rs]
   [ui.components.alert.view :as alert]
   [ui.components.modal.view :as modal]
   [ui.components.toggle.view :as toggle]
   [ui.dom-globals :as dg]
   [ui.editors.data-input :as data-input]))

(defonce modal-key :entity-modal)

(def min-spinner-time-in-ms 300)

(defn create-sort-lookup
  [sorted-list]
  (->> sorted-list
       (map-indexed (fn [idx k]
                      [k idx]))
       (into {})))

(defn default-ord-compare
  [a b]
  (let [attr-a (:attribute a)
        attr-b (:attribute b)
        def-a (:field/default-ord a)
        def-b (:field/default-ord b)
        res (cond (and (nil? def-a) (nil? def-b))
                  (compare attr-a attr-b)
                  (nil? def-a)
                  1
                  (nil? def-b)
                  -1
                  :else
                  (compare def-a def-b))]
    res))

(defn sort-by-index
  [order-lookup a b]
  (let [attr-a (:attribute a)
        attr-b (:attribute b)
        idx-a (order-lookup attr-a)
        idx-b (order-lookup attr-b)
        res (cond (and (nil? idx-a) (nil? idx-b))
                  (default-ord-compare a b)
                  (nil? idx-a)
                  1
                  (nil? idx-b)
                  -1
                  :else
                  (compare idx-a idx-b))]
    res))

(defn mandatory-compare
  [mandatory-fields a b]
  ;; TODO easy performance improvement if mandatory fields is an sort-index lookup
  (let [a-attrib (:attribute a)
        b-attrib (:attribute b)
        a-index (fu/index-in a-attrib mandatory-fields)
        b-index (fu/index-in b-attrib mandatory-fields)
        res (cond
              (and a-index b-index)
              (compare a-index b-index)
              a-index
              -1
              b-index
              1
              :else
              (compare (or (fu/index-of mandatory-fields a-attrib)
                           (:field/default-ord a))
                       (or (fu/index-of mandatory-fields b-attrib)
                           (:field/default-ord b))))]
    res))

(defn hidden-editor-type?
  [{:keys [field/default-visibility] :as _field-schema}]
  (= (:editor default-visibility) :editor.type/none))

(defn write-authorization?
  [{:field/keys [default-authorization] :as _field-schema}]
  (let [required-role (->> default-authorization
                           :access/write)]
    (isa? roles/role-hierarchy @session/role required-role)))

(defn error-link
  [close-modal! on-click link-text]
  [:a
   {:href     "#"
    :on-click (fn [e]
                (close-modal!)
                (on-click e))}
   link-text])

(defn field-write-access?
  [{:keys [live-updates-state selected-pipeline-id]}
   {:keys [field/attribute containing-entity-type] :as _field-schema}]
  (f-permissions/field-write-access?
   attribute
   containing-entity-type
   (or selected-pipeline-id
       (fu/pipeline-id @live-updates-state))))

(defn entity-editor
  [state error-state live-updates-state
   {:keys [full-entity-schema
           primary-schema?
           entity-type
           entity-atom
           mandatory-fields
           selected-pipeline-id
           custom-order
           close-modal!]}]
  (let [tenant @session/tenant
        entity-template (some->> full-entity-schema
                                 (remove hidden-editor-type?)
                                 (filter write-authorization?)
                                 (filter (partial field-write-access?
                                                  {:live-updates-state   live-updates-state
                                                   :selected-pipeline-id selected-pipeline-id})))

        entity-template-lookup (->> entity-template
                                    (map (fn [{:keys [attribute] :as field-schema}]
                                           [attribute field-schema]))
                                    (into {}))
        all-fields (->> entity-template
                        (sort (or custom-order
                                  (partial sort-by-index (create-sort-lookup mandatory-fields)))))
        pipeline-key (pipeline/pipeline-key entity-type)
        pipeline (get @session/entities (get @entity-atom pipeline-key))
        show-field?
        (fn
          [{:keys       [attribute field/secondary-field?]
            editor-type :editor/type
            :as         field-schema}]
          (let [entity @entity-atom]
            (and (let [in-use-filter? (:field/in-use-for-this-data? field-schema)]
                   (or (not (fn? in-use-filter?))
                       (in-use-filter? entity
                                       @session/entity-types
                                       session/has-feature-access?
                                       session/has-entity-access?)))
                 (not secondary-field?)                                                             ;; secondary fields are shown in related fields in editor
                 (not= editor-type :editor.type/none)
                 (or (not= attribute :common/related-entities-commitments)
                     (and (plan/available :feature/fundraising @session/tenant @session/role)
                          (session/has-module-access? @session/account :module/fundraising))))))
        visible-fields (->> all-fields
                            (filter show-field?))

        field-count (count visible-fields)]
    [:div
     {:class "flex flex-col gap-1"}
     (doall
      (->>
       visible-fields
       (map-indexed
        (fn [idx
             {:keys       [attribute field/aggregate-attribute field/particular-value]
              editor-type :editor/type
              :as         field-schema}]
          (let [entity @entity-atom
                resolve-value (fn []
                                (if (and aggregate-attribute (fn? particular-value))
                                  (set (particular-value (get @entity-atom aggregate-attribute)
                                                         @session/entities))
                                  (get @entity-atom attribute)))
                value (resolve-value)
                field-custom-params (:field/custom-params field-schema)]
            ^{:key (str "entity-creator-editor-" attribute)}
            [field-editor/view
             (merge
              field-schema
              {:id                    (str "entity-creator-editor-" attribute)
               :auto-focus            (and primary-schema?
                                           (zero? idx))
               :creation-pipeline-id  selected-pipeline-id
               :drag-options          {:hide-drag-area? true}
               :state-atom            state                                                         ;; state is passed  from outside so debugging is easier
               :value                 value
               :close-menu-on-scroll? true
               :menu-portal-target    (dg/element-by-id "react-select-menu-container")
               :entity-types-atom     session/entity-types
               :read-only?            (not (some #{(:access/type field-schema)}
                                                 [:field.access/write :field.access/create]))
               ;; TODO: take language from user / tenant config -> default to :en
               ;; if only :en in field/translations use :en
               :label                 (let [fs (registry/get-field-schema @session/tenant-schema
                                                                          attribute)
                                            target-asset-types? (= attribute
                                                                   :pipeline/target-asset-types)
                                            pipeline-entity-type (:pipeline/entity-type
                                                                  @entity-atom)
                                            sales-pipeline? (and (= entity-type :entity.type/pipeline)
                                                                 (= pipeline-entity-type
                                                                    :entity.type/sales-opportunity))
                                            fs (cond-> fs
                                                 (and target-asset-types? sales-pipeline?)
                                                 (assoc-in [:field/translations
                                                            :en] (loc/tr :customer-types)))]
                                        (loc/field-name attribute fs))                              ;; we select label outside-of field-editor, so we can customize label if needed
               :mandatory?            (boolean (some #{attribute} mandatory-fields))                ;; can be function or boolean - most likely always view dependent
               ;; tools are configured by need - all handlers are inside as currently the tools have no side effects
               :tools                 [:tool-item/cancel :tool-item/clear]
               :error-text            nil                                                           ;; TODO: use editor-atom to store validation and save errors
               ;;note: add other rules as needed:
               :validate-on-start?    (and (= attribute :person/universal-email-address)
                                           (not (cu/blank-value? value)))
               :validate!
               (fn [updated-attribute new-value]
                 (let [field-constraints (when (= attribute updated-attribute)
                                           (:field/constraints field-schema))
                       errors (constraints/validate field-constraints
                                                    (:db/id entity)
                                                    new-value
                                                    entity
                                                    (partial session/unique-check
                                                             false
                                                             (fn [entity-id]
                                                               (close-modal!)
                                                               (routing/open-entity entity-id)))
                                                    loc/tr
                                                    js/console.log)
                       ;;skip person email validation if value is empty
                       errors (if (or (and (= :person/universal-email-address updated-attribute)
                                           (= "" new-value))
                                      (and (= :person/work-email-address updated-attribute)
                                           (= "" new-value)))
                                nil
                                errors)]
                   (if errors
                     (swap! error-state assoc updated-attribute errors)
                     (swap! error-state dissoc updated-attribute))

                   (cond
                     (cu/empty-value? new-value)
                     (swap! live-updates-state dissoc updated-attribute)
                     (or (not (contains?
                               #{:editor.type/text-simple
                                 :editor.type/integer
                                 :editor.type/multiplier
                                 :editor.type/textarea
                                 :editor.type/wysiwyg
                                 :editor.type/percentage
                                 :editor.type/number
                                 :editor.type/url}
                               editor-type))
                         ;; for text input editor we do not want to update the live-updates every time
                         ;; TODO: when create flow is redone, the whole form validation should be redone
                         (and (not errors)
                              (cu/blank-value? (get @live-updates-state updated-attribute))))
                     (swap! live-updates-state assoc updated-attribute new-value))

                   errors))
               :on-save!
               (fn [saved-attribute editor-new-value on-success! _on-error!]
                 (let [edited-value
                       (if-not aggregate-attribute
                         editor-new-value
                         (let [new-value (set editor-new-value)
                               old-value (resolve-value)
                               added-values (set/difference new-value old-value)
                               removed-values (set/difference old-value new-value)
                               old-aggregate-value (get @entity-atom aggregate-attribute)]
                           (-> old-aggregate-value
                               (set/union added-values)
                               (set/difference removed-values))))
                       save-attribute (or aggregate-attribute saved-attribute)
                       previous-anchor (:reminder-rule/reminder-anchor @entity-atom)]
                   ; (when-not (or (nil? edited-value) ;;TODO: commented to enable clearing values - verify it works !!
                   ;              (and (string? edited-value) (empty? (string/trim edited-value))))
                   (swap! entity-atom assoc save-attribute edited-value)

                   ;; Which side of its anchor a reminder sits on depends on the
                   ;; anchor: you remind people ahead of a deadline, but only
                   ;; after a batch send or a previous reminder. That default
                   ;; cannot be expressed statically - `reset-state!` runs before
                   ;; the anchor is chosen - so it is applied when the anchor is
                   ;; picked, in the same place the currency companion default
                   ;; below is applied.
                   ;;
                   ;; It only overwrites a direction that is still whatever the
                   ;; previous anchor implied, so a direction the user chose by
                   ;; hand survives a later change of anchor.
                   (when (= save-attribute :reminder-rule/reminder-anchor)
                     (let [current (:reminder-rule/offset-direction @entity-atom)]
                       (when (or (nil? current)
                                 (= current
                                    (reminder-rule-options/default-offset-direction previous-anchor)))
                         (swap! entity-atom assoc
                                :reminder-rule/offset-direction
                                (reminder-rule-options/default-offset-direction edited-value)))))

                   (when-let [sa (:field/secondary-attribute field-schema)]
                     (when (and sa
                                (not= sa saved-attribute)
                                (some #{sa} (into [] mandatory-fields))
                                (not (get @entity-atom sa))
                                (= :data.type/currency-value (:field/data-type field-schema)))
                       (when-let [default-currency (:tenant/default-currency @session/tenant)]
                         (swap! entity-atom assoc sa default-currency))))
                   (deref entity-atom)
                   (on-success!)))}
              (when (>= idx (- field-count 1))
                {:menu-placement "top"})
              (when (= attribute :person/universal-email-address)
                {:error-link (partial error-link close-modal!)})
              (when-not (:placeholder field-schema)
                {:placeholder (field/placeholder field-schema)})
              (when-let [option-filter-fn (:field/option-filter-fn field-schema)]
                {:field/option-filter-fn (fn [filter-args]
                                           (let [filtered (option-filter-fn
                                                           (merge filter-args
                                                                  {:tenant tenant
                                                                   :containing-entity
                                                                   @entity-atom}))]
                                             filtered))})
              ;; The OpenSearch counterpart of :field/option-filter-fn above. Without
              ;; it a field that declares an os-filter-terms-fn is searched
              ;; unfiltered, because the fn never reaches open-search!. Read from
              ;; the registry rather than `field-schema`: the latter is the entity
              ;; template, and a function does not survive the trip through the
              ;; serialized tenant schema it is built from - the same reason the
              ;; :label lookup above goes back to the registry.
              (when-let [os-filter-terms-fn (:field/os-filter-terms-fn
                                             (registry/get-field-schema @session/tenant-schema
                                                                        attribute))]
                {:field/os-filter-terms-fn (fn [search-ctx]
                                             (os-filter-terms-fn
                                              (merge search-ctx
                                                     {:tenant            tenant
                                                      :containing-entity @entity-atom})))})
              ;; Bound here for the same reason as the two filter fns above: the
              ;; function lives in the registry, and the containing entity is
              ;; only known to this modal. Lets a picker create its target
              ;; already attached to whatever is being created here.
              (when-let [create-start-value-fn (:field/create-start-value-fn
                                                (registry/get-field-schema @session/tenant-schema
                                                                           attribute))]
                {:field/create-start-value-fn (fn [seed-args]
                                                (create-start-value-fn
                                                 (merge seed-args
                                                        {:tenant            tenant
                                                         :containing-entity @entity-atom})))})
              (when-let [secondary-attribute (:field/secondary-attribute field-schema)]             ;; secondary attribute
                {:secondary-view-args
                 (let [sec-templ (get entity-template-lookup secondary-attribute)]
                   (merge
                    sec-templ
                    {:state-atom         state
                     :value              (or (get @entity-atom secondary-attribute)
                                             (when (= (:field/data-type sec-templ)
                                                      :data.type/currency-symbol)
                                               (:tenant/default-currency @session/tenant)))
                     :menu-portal-target (dg/element-by-id "react-select-menu-container")
                     :label              (loc/tr secondary-attribute)                                   ;; when we have custom dynamic fields the labels come from tenant configuration
                     :mandatory?         (boolean (some #{attribute} mandatory-fields))
                     :error-text         nil}))})
              (when (fn? field-custom-params)
                (field-custom-params entity))
              (when (and pipeline
                         (pipeline/stage-attribute? attribute))
                {:containing-entity @entity-atom}))])))))]))

(defn editor-body
  [{:keys [optional-template target-asset-entity-types]}]
  (let [hide-alert! identity
        first-optional (first optional-template)
        rest-optional (rest optional-template)
        ;;add currency-symbol if first one happens to be a currency field:
        secondary (-> first-optional
                      :field/secondary-attribute)
        secondary-fs (->> rest-optional
                          (filter (fn [{:keys [attribute]}]
                                    (= attribute secondary)))
                          first)
        rest-optional (->> rest-optional
                           (remove (fn [fs]
                                     (= fs secondary-fs))))
        first-optional-template (concat [first-optional]
                                        (when secondary-fs
                                          [secondary-fs]))]
    (fn [{:keys [state error-state live-updates-state draft-state create-template
                 selected-pipeline-id custom-order entity-type optional-template
                 mandatory-fields start-value close-modal!]}]
      (let [{:keys [alert show-all?]} @state]
        [:div
         (when alert
           [:div.form-group
            [alert/danger
             {:on-close hide-alert!}
             alert]])
         [entity-editor state error-state live-updates-state
          {:full-entity-schema        create-template
           :primary-schema?           true
           :entity-atom               draft-state
           :entity-type               entity-type
           :custom-order              custom-order
           :mandatory-fields          mandatory-fields
           :selected-pipeline-id      selected-pipeline-id
           :target-asset-entity-types target-asset-entity-types
           :start-value               start-value
           :close-modal!              close-modal!}]
         (when optional-template
           [entity-editor state error-state live-updates-state
            {:full-entity-schema   (if-not show-all?
                                     first-optional-template
                                     optional-template)
             :entity-atom          draft-state
             :entity-type          entity-type
             :custom-order         custom-order
             :mandatory-fields     mandatory-fields
             :selected-pipeline-id selected-pipeline-id
             :start-value          start-value
             :close-modal!         close-modal!}])
         (when (and optional-template
                    (not-empty rest-optional)
                    (not show-all?))
           [:div.text-left.pt-2
            [:span.show-all.px-0
             {:on-click (fn [e]
                          (swap! state assoc :show-all? true)
                          (dg/prevent-and-stop e))}
             (loc/tr :more-properties)]])]))))

(defn- error->text
  "Plain text of one validation error, which is a string or a hiccup element.
   Nil when a hiccup element cannot be rendered, so that the tooltip never
   breaks the footer."
  [error]
  (if (string? error)
    error
    (try
      (fu/html->plain-text (rs/render-to-string error))
      (catch :default _
        nil))))

(defn- error-texts
  "Plain text of each validation error, without the blank ones."
  [errors]
  (->> errors
       (keep error->text)
       (remove string/blank?)
       vec))

(defn- errors-tooltip
  "Tooltip of the disabled Create button: one line for each reason why the form
   cannot be submitted. Nil when there is no reason to show."
  [texts]
  (when (seq texts)
    (into [:div]
          (map-indexed (fn [idx text]
                         ^{:key idx}
                         [:div text]))
          texts)))

(defonce logged-error-texts (atom nil))

(defn- log-error-texts!
  "Writes the reasons why the Create button is disabled to the console, once for
   each change of the reasons. The footer renders often, so it must not log on
   every render."
  [texts]
  (when (not= texts @logged-error-texts)
    (reset! logged-error-texts texts)
    (when (seq texts)
      (js/console.log "Create button disabled, reasons:" (clj->js texts)))))

(defn editor-footer
  [{:keys [state draft-state alert-state props close! processed! raw-schema]}]
  (let [{:keys [on-processed! entity-type show-error-view? ok-button-icon ok-button-text]} props
        errors (events/validate-form state raw-schema)
        errors (cond
                 (nil? errors)
                 errors

                 (and (coll? errors)
                      (keyword? (first errors)))
                 [errors]

                 :else
                 (seq
                  errors))
        texts (error-texts errors)
        _ (log-error-texts! texts)]
    [:<>
     [modal/left-cancel-only close! "create-cancel-button"]
     [modal/right-single-button
      nil
      (seq errors)
      (fn [_]
        (swap! state assoc
               :processing true
               :start-time (zap-time/now))
        (events/on-submit!
         state
         draft-state
         (fn [new-entity _]
           (let [new-entity-id (:db/id new-entity)]
             (js/console.log "Submit create response : " new-entity)
             (swap! state assoc
                    :new-entity new-entity
                    :db/id new-entity-id)

             (if on-processed!
               (on-processed! {:new-entity-id new-entity-id
                               :new-entity    new-entity
                               :close-cb!     (fn []
                                                (processed! {:new-entity-id new-entity-id
                                                             :new-entity    new-entity
                                                             :close-cb!     close!}))})
               (processed! {:new-entity-id new-entity-id
                            :new-entity    new-entity
                            :close-cb!     close!}))))
         (fn [error]
           (swap! state dissoc :processing)
           (swap! state assoc :start-time 0)
           (when show-error-view?
             (swap! state assoc :error-view? true))
           (swap! alert-state assoc :error error)
           (logging/error "Failed NEW " entity-type " -> " error))))
      [:<>
       (when ok-button-icon
         ok-button-icon)
       [:span (or ok-button-text (loc/tr :submit))]]
      "create-ok-button"
      (errors-tooltip texts)]]))

(defn viewer-body
  [state props]
  (let [{:keys [entity-type]} props
        entity (get @session/entities (:db/id @state))
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        enriched-entity (ic/enrich-entity entity)
        hide-keys (concat (keys entity)
                          [:person/avatar-url                                                       ;;TODO more generic way to list hidden intelligence fields
                           :person/gender
                           :company/logo])
        intelligence-data (apply dissoc enriched-entity hide-keys)]
    [:<>
     [:div.create-modal__body
      (if (and intelligence-data
               (seq intelligence-data))
        [:div.create-intelligence
         [:div.create-intelligence__right
          [:h2 [:span [:i.fal.fa-exclamation-circle] (loc/tr :create-entity-found-intelligence)]]
          [:h4 (loc/tr :create-entity-found-intelligence-disclaimer)]
          [:ul
           (doall
            (for [[k v] intelligence-data
                  :let [display-value (fu/display-data
                                       tenant-schema
                                       tenant
                                       k
                                       (registry/get-field-schema tenant-schema k)
                                       intelligence-data
                                       :passed-value
                                       v)]]
              [:li {:key (str "intelligence-data-" k)}
               (str (loc/tr k) " : " display-value)]))]]]
        (if (and (or (:company/website entity)
                     (:person/universal-email-address entity))
                 (> (- (zap-time/now)
                       (:success-time @state))
                    5000))
          (loc/tr :enriching-data)
          [:p
           {:class "p-6 flex flex-col gap-4 justify-center items-center text-bold"}
           [:img
            {:src   "/img/sent-success.png"
             :class "max-w-48"}]
           (loc/tr :entity-created-successfully [(loc/tr entity-type)])]))]]))

(defn error-body
  [alert-state props]
  (let [{:keys [error-view-text]} props
        error-view-text (or error-view-text
                            (:message (:error @alert-state))
                            [:span (loc/tr :something-went-wrong)
                             [:a {:href (str "mailto:" (loc/tr :support-email-address))}
                              (loc/tr :support-email-address)]])]
    [:<>
     [:div.create-modal__body
      error-view-text]]))

(defn viewer-footer
  [done!]
  [:div
   {:class "ml-auto"}
   [modal/right-single-button
    nil
    false
    done!
    (loc/tr :close)
    nil]])

(defn error-footer
  [_state close-modal!]
  [modal/right-single-button
   nil
   false
   close-modal!
   (loc/tr :continue)
   nil])

(defn pipeline-view-body
  [{:keys [entity-type draft-state live-updates-state state]}]
  (let [pipelines (case entity-type
                    :entity.type/oppo @session/oppo-pipelines
                    :entity.type/fundraising-opportunity @session/froppo-pipelines
                    :entity.type/sales-opportunity @session/sales-oppo-pipelines
                    :entity.type/exit-opportunity @session/exit-oppo-pipelines
                    nil)
        show-archived? (:show-archived? @state)
        pipeline-key (pipeline/pipeline-key entity-type)
        resolved-pipelines (if (or show-archived?
                                   (fu/all-pipelines-inactive? pipelines))
                             pipelines
                             (->> pipelines
                                  (remove :pipeline/inactive?)))
        pipelines-options (->> resolved-pipelines
                               (map (fn [pipeline]
                                      {:value (:db/id pipeline)
                                       :label (entity/display-string pipeline)})))
        entity-type-label (string/lower-case (loc/tr entity-type))
        some-pipelines-inactive? (and (fu/inactive-pipelines? pipelines)
                                      (not (fu/all-pipelines-inactive? pipelines)))]
    [:<>
     [:div.pb-5.px-3

      (when (fu/all-pipelines-inactive? pipelines)
        [:div.infobox.infobox--warning.mb-3
         [:i.fas.fa-info-circle.infobox__icon.mr-3]
         [:span.infobox__message
          (loc/tr :all-pipelines-inactive-info [entity-type-label])]])
      [:div.d-flex.align-items-center.justify-content-between
       [:p.d-block.input-editor__label.pb-2
        (loc/tr :choose-pipeline [entity-type-label])]

       (when some-pipelines-inactive?
         [:div.d-flex.align-items-center.ml-3.pb-2
          [toggle/toggle
           {:checked?  (boolean show-archived?)
            :on-change (fn [on?]
                         (swap! state assoc :show-archived? on?))}]
          [:span.d-inline-block.input-editor__label.mx-2 (loc/tr :show-archived-pipelines)]])]

      (when (and some-pipelines-inactive?
                 show-archived?)
        [:div.infobox.infobox--warning.mb-3
         [:i.fas.fa-info-circle.infobox__icon.mr-3]
         [:span.infobox__message.pt-1 (loc/tr :some-pipelines-inactive-info [entity-type-label])]])

      [:div.select-area
       [data-input/view
        {:editor-type   :editor.type/select
         :data-type     :data.type/ref-single
         :is-clearable? false
         :value         (get @draft-state pipeline-key)
         :options       pipelines-options
         :on-change     (fn [new-value & _]
                          (swap! draft-state assoc pipeline-key new-value)
                          (swap! live-updates-state assoc pipeline-key new-value))}]]]]))

(def tesi-sorted-lookup
  (create-sort-lookup (vec (concat attributes/tesi-company-attrs
                                   attributes/tesi-deal-attrs
                                   attributes/tesi-investor-attrs
                                   attributes/tesi-holding-attrs))))

(def f-log-sorted-lookup
  (create-sort-lookup (vec (concat attributes/f-log-company-attrs
                                   attributes/f-log-deal-attrs))))

(def voima-sorted-lookup
  (create-sort-lookup (vec (concat attributes/voima-mandatory-deal-attrs))))

(def eq-sorted-lookup
  (create-sort-lookup (vec (concat attributes/eq-fof-asset-attrs))))

(defn pipeline-view-footer
  [{:keys [close-modal! draft-state live-updates-state
           entity-type on-confirm!]}]
  (let [pipeline-key (pipeline/pipeline-key entity-type)
        pipeline-is-not-selected? (and (nil? (get @draft-state pipeline-key))
                                       (nil? (get @live-updates-state pipeline-key)))]
    [:<>
     [modal/left-cancel-only close-modal! nil]
     [modal/right-single-button
      nil
      pipeline-is-not-selected?
      on-confirm!
      (loc/tr :confirm-and-continue)
      nil]]))

(defn view
  [{:keys [state props _shown? _close-modal! _topmost?]}]
  (swap! state assoc :processing true)
  (let [{:keys [entity-type initial-values creation-pipeline-id                                                                ;;explicitly passed pipeline used for access checks
                dispatch-on-create dispatch-on-create-nev-2024 named-value-attribute start-value]} props
        draft-state (r/cursor state [:draft-entity])
        error-state (r/cursor state [:errors])
        live-updates-state (r/cursor state [:live-updates])
        alert-state (r/cursor state [:server-errors])
        target-assets-in-use (some #{(:pipeline/entity-type start-value)}
                                   [:entity.type/oppo
                                    :entity.type/sales-opportunity])
        reset-state!
        (fn []
          (let [resolved-mandatory-fields (events/resolve-mandatory-fields
                                           (assoc props
                                                  :draft-state draft-state
                                                  :state state
                                                  :target-assets-in-use target-assets-in-use))]
            (events/reset-state! {:state                state
                                  :error-state          error-state
                                  :live-updates-state   live-updates-state
                                  :draft-state          draft-state
                                  :alert-state          alert-state
                                  :entity-type          entity-type
                                  :mandatory-fields     resolved-mandatory-fields
                                  :attribute            named-value-attribute
                                  :creation-pipeline-id creation-pipeline-id
                                  :initial-values       initial-values
                                  :start-value          start-value})))
        show-pipeline-selector? (events/pipeline-view? entity-type creation-pipeline-id)]
    (when show-pipeline-selector?
      (swap! state assoc :pipeline-selection-needed? true))

    (if show-pipeline-selector?
      (events/reset-pipeline-state! {:state                state
                                     :draft-state          draft-state
                                     :live-updates-state   live-updates-state
                                     :entity-type          entity-type
                                     :creation-pipeline-id creation-pipeline-id})
      (reset-state!))

    (fn [{:keys [shown? close-modal! props state topmost?]}]
      (let [{:keys [entity-type on-cancel! on-ok! title-text
                    ;;explicitly passed pipeline used for access checks:
                    creation-pipeline-id
                    override-editor-types
                    skip-success-msg? deal-pipeline? named-value-attribute start-value
                    reset-focus!]} props
            selected-pipeline-id (events/resolve-selected-pipeline-id
                                  {:entity-type          entity-type
                                   :creation-pipeline-id creation-pipeline-id
                                   :draft-state          draft-state})
            capman-re? (and (tenant-customizations/capman?)
                            (tenant-customizations/capman-is-real-estate-pipeline?
                             selected-pipeline-id))
            resolved-mandatory-fields (events/resolve-mandatory-fields
                                       (assoc props
                                              :draft-state draft-state
                                              :state state
                                              :target-assets-in-use target-assets-in-use))
            close! (fn []
                     (when on-cancel!
                       (on-cancel!))
                     (close-modal!)
                     (when reset-focus!
                       (reset-focus!)))
            schema (registry/get-schema @session/tenant-schema entity-type)
            custom-order
            (cond
              (tenant-customizations/tesi?)
              (when (contains? #{:entity.type/oppo :entity.type/company :entity.type/investor
                                 :entity.type/holding}
                               entity-type)
                (partial sort-by-index tesi-sorted-lookup))

              (tenant-customizations/f-log?)
              (when (contains? #{:entity.type/oppo :entity.type/company} entity-type)
                (partial sort-by-index f-log-sorted-lookup))

              (tenant-customizations/voima-ventures?)
              (when (contains? #{:entity.type/oppo} entity-type)
                (partial sort-by-index voima-sorted-lookup))

              (tenant-customizations/eq-varainhoito?)
              (when (contains? #{:entity.type/fof-asset :entity.type/asset} entity-type)
                (partial sort-by-index eq-sorted-lookup)))

            target-asset-entity-types (when (events/pipeline-view-selected-pipeline? draft-state)
                                        (->> (session/get-entity selected-pipeline-id)
                                             :pipeline/target-asset-types))
            raw-schema (schema/raw-schema {:entity-type               entity-type
                                           :schema                    schema
                                           :target-assets-in-use      target-assets-in-use
                                           :start-value               start-value
                                           :named-value-attribute     named-value-attribute
                                           :deal-pipeline?            deal-pipeline?
                                           :capman-re?                capman-re?
                                           :target-asset-entity-types target-asset-entity-types})
            entity-create-attributes (create-attributes/entity-create-attributes
                                      {:entity-type          entity-type
                                       :schema               schema
                                       :mandatory-fields     resolved-mandatory-fields
                                       :capman-re?           capman-re?
                                       :selected-pipeline-id selected-pipeline-id})
            full-schema
            (some->>
             raw-schema
             (filter (fn [[attribute _field-schema]]
                       (let [has-access? (f-permissions/field-read-access?
                                          attribute
                                          entity-type
                                          selected-pipeline-id)]
                         has-access?)))
             (remove (fn-> first
                           #{:oppo/pass-reason-v2 :oppo/pass-reason-secondary
                             :oppo/on-hold-until :oppo/cur-broken-deal-expenses}))
             (remove (fn [[attr _fs]]
                       (and (= attr :fund/reporting-admins)
                            (not (session/lp-reporting?)))))
             (map
              (fn [[attribute field-schema]]
                (merge field-schema
                       {:attribute   attribute
                        :access/type :field.access/write
                        :editor/type (entity-utils/resolve-editor-type attribute field-schema)}))))
            entity-template
            (some->>
             full-schema
             (remove
              (fn [field-schema]
                (let [attribute (:attribute field-schema)
                      visibility (get override-editor-types attribute)
                      visibility (or visibility
                                     (->> field-schema
                                          :field/default-visibility
                                          :editor))
                      hide?
                      (and
                       (= visibility :editor.type/none)
                       (cond
                         (tenant-customizations/capman?)
                         (not (some #{(:attribute field-schema)} [:fundraising-opportunity/investor]))

                         :else
                         true))]
                  hide?))))
            create-template
            (some->>
             entity-template
             (filter #(some #{(:attribute %)} entity-create-attributes))
             (map
              (fn [{:keys [attribute] :as e}]
                (let [visibility (:field/default-visibility e)]
                  (merge e
                         {:field/default-ord (or (cu/index-of entity-create-attributes attribute)
                                                 (:field/default-ord e))}
                         (when (= :editor.type/none (:editor visibility))
                           {:field/default-visibility (assoc visibility
                                                             :editor
                                                             (:grid visibility))
                            :editor/type              (:grid visibility)})
                         (when (= :company/website attribute)
                           {:info-text (loc/tr :create-company/website-info)})
                         (when (= :kyc-profile/target attribute)
                           {:info-text (loc/tr :create-kyc-profile/target-info)})
                         (when (= :fundraising-opportunity/investor attribute)
                           {:editor/type              :editor.type/select
                            :field/default-visibility (assoc visibility :editor :editor.type/select)})
                         (when (and (= :person/universal-email-address attribute)
                                    (or (not resolved-mandatory-fields)
                                        (not (some #{attribute} resolved-mandatory-fields))))
                           {:info-text (loc/tr :create-person/email-info)}))))))
            remaining-fields
            (when (some #{entity-type}
                        ;; show 'More properties' link for these entity-types
                        (concat [:entity.type/company :entity.type/oppo :entity.type/fof-asset]
                                (when-not (tenant-customizations/capman?)
                                  [:entity.type/real-estate])))
              (some->> entity-template
                       (remove #(some #{(:attribute %)} entity-create-attributes))))
            processed! (fn [{:keys [new-entity]}]
                         (js/console.log "New entity processed : " new-entity " << " skip-success-msg?)
                         (swap! state assoc :success-time (zap-time/now))
                         (when dispatch-on-create
                           (rf/dispatch dispatch-on-create))

                         ;; HACK: to prevent regression bugs while waiting for new entity creator
                         (when dispatch-on-create-nev-2024
                           (rf/dispatch (conj dispatch-on-create-nev-2024 {:entity (session/get-entity (:db/id @state))})))

                         (if skip-success-msg?
                           (if on-ok!
                             (on-ok! {:new-entity-id (:db/id new-entity)
                                      :new-entity    new-entity
                                      :close-cb!     close-modal!})
                             (close-modal!))
                           (swap! state dissoc :processing)))
            finished? (and (:db/id @state)
                           (not (:processing @state)))
            processing?
            (or (:processing @state)
                (when (and skip-success-msg?
                           (< (- (zap-time/now) (:start-time @state)) min-spinner-time-in-ms))
                  (let [since-start (-> min-spinner-time-in-ms
                                        (- (zap-time/now))
                                        (+ (:start-time @state)))
                        timeout (max 100 since-start)]
                    (js/setTimeout #(swap! state assoc :start-time 0) timeout)
                    true)))
            error-view? (:error-view? @state)
            show-pipeline-selector? (and (events/pipeline-view? entity-type creation-pipeline-id)
                                         (:pipeline-selection-needed? @state))
            done! (fn []
                    (let [new-entity (:new-entity @state)]
                      (if on-ok!
                        (on-ok! {:new-entity-id (:db/id new-entity)
                                 :new-entity    new-entity
                                 :close-cb!     close-modal!})
                        (close-modal!))))]
        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         (if finished? done! close!)
          :backdrop      "static"
          :close-button? (not finished?)}
         (or title-text (loc/tr :create-entity/title [(loc/tr (:entity-type @state))]))
         [:<>
          (cond
            processing?
            (if (or (seq (:company/website (:draft-entity @state)))
                    (seq (:person/universal-email-address (:draft-entity @state))))
              [syncing/sync-content]
              [:div.spinner-wrapper
               [:i.fal.fa-spinner.fa-pulse.fa-4x]
               [:span.processing-label (loc/tr :processing)]])

            show-pipeline-selector?
            [pipeline-view-body
             {:entity-type        entity-type
              :draft-state        draft-state
              :live-updates-state live-updates-state
              :state              state}]

            finished?
            [viewer-body state props]

            error-view?
            [error-body alert-state props]

            :else
            [editor-body
             {:state                     state
              :error-state               error-state
              :live-updates-state        live-updates-state
              :draft-state               draft-state
              :create-template           create-template
              :selected-pipeline-id      selected-pipeline-id
              :creation-pipeline-id      selected-pipeline-id
              :target-asset-entity-types target-asset-entity-types
              :entity-type               entity-type
              :optional-template         remaining-fields
              :mandatory-fields          resolved-mandatory-fields
              :custom-order              custom-order
              :start-value               start-value
              :close-modal!              close-modal!}])]
         [:<>
          (cond
            finished?
            [viewer-footer done!]

            processing?
            nil

            error-view?
            [error-footer state
             (fn []
               (swap! state dissoc :error-view?)
               (deref state))]

            show-pipeline-selector?
            [pipeline-view-footer
             {:close-modal!       close-modal!
              :state              state
              :draft-state        draft-state
              :live-updates-state live-updates-state
              :entity-type        entity-type
              :on-confirm!        (fn [_]
                                    ;; this assumes that selected pipeline
                                    ;; is properly set in state and resolved for render function
                                    (swap! state assoc :processing true)
                                    (reset-state!)
                                    (swap! state dissoc :pipeline-selection-needed?))}]
            :else
            [editor-footer
             {:state       state
              :draft-state draft-state
              :alert-state alert-state
              :props       (assoc props :show-error-view? true)
              :raw-schema  raw-schema
              :close!      close!
              :processed!  processed!}])]]))))
